// Таблицата на справката „Доставки": ниски редове на един ред. Широк екран —
// № · Дата · Документ · Доставчик · Склад · Въвел · Връзки · Бележка · Нето ·
// Бруто · Плащане · Печат/⋯; лаптоп/таблет — № · Дата · Документ (складът,
// въвелият и бележката — приглушено до номера) · Доставчик · Бруто · Плащане ·
// Печат/⋯; телефон — карти (бележката — втори ред, „⋯" вдясно).
// Филтрите са в заглавията на колоните (на средна ширина „Документ" носи и
// Склад/Въвел/Фактура — те се виждат в неговата клетка); Дата/Документ/
// Доставчик/Бруто сортират. „Връзки": фактура → стоковите ѝ; стокова → фактурата
// ѝ, „чака фактура" (кехлибарено) или „приключена без фактура" (неутрално).
// Анулираните са зачертани. ↑/↓ по редовете, Enter отваря.
import type { ReactNode } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { Delivery } from "../../../../api/inventory";
import { formatMoney } from "../../components/numbers";
import { sectionList, type ColumnSections } from "../../components/filters/useColumnFilters";
import { RowActionsMenu, type RowAction } from "../../components/list/RowActionsMenu";
import { DeliveryPaymentPill, DeliveryTypeBadge } from "../../delivery/components/DeliveryBadges";
import {
  deliveryGrossOf,
  deliveryNetOf,
  isDeliveryAnnulled,
  noteInvoiceStateOf,
  warehouseNamesOf,
} from "../../delivery/components/deliveryMeta";
import { ReportHead } from "../components/ReportListBits";
import {
  REPORT_HEADER_ROW_CLASS,
  REPORT_OPENABLE_ROW_CLASS,
  moveRowFocus,
  openableRowProps,
} from "../components/reportListModel";
import type { DeliveryFilterKey, DeliverySort, DeliverySortKey } from "./deliveriesReportModel";

export type DeliveriesDensity = "full" | "medium" | "cards";

// Литерални класове — Tailwind сканира само пълни низове
const GRID_CLASS = {
  full: "grid grid-cols-[44px_84px_minmax(130px,1fr)_minmax(130px,1.1fr)_minmax(80px,.7fr)_minmax(76px,.6fr)_minmax(110px,.9fr)_minmax(100px,1fr)_92px_100px_120px_96px] items-center gap-2.5 px-[18px]",
  medium: "grid grid-cols-[48px_84px_minmax(170px,1.4fr)_minmax(120px,1fr)_100px_120px_96px] items-center gap-2.5 px-[18px]",
} as const;

// Колко стокови се показват като значки във „Връзки" (останалите — „+N")
const MAX_LINKED_NOTES = 2;

type Props = {
  rows: Delivery[];
  density: DeliveriesDensity;
  sort: DeliverySort;
  onSort: (key: DeliverySortKey) => void;
  filters: ColumnSections<DeliveryFilterKey>;
  onOpen: (delivery: Delivery) => void;
  onPrint: (delivery: Delivery) => void;
  actionsOf: (delivery: Delivery) => RowAction[];
  footer?: ReactNode;
};

export const DeliveriesReportTable = ({
  rows,
  density,
  sort,
  onSort,
  filters,
  onOpen,
  onPrint,
  actionsOf,
  footer,
}: Props) => {
  const { t } = useTranslation();

  const numberClass = (delivery: Delivery) =>
    isDeliveryAnnulled(delivery) ? "text-faint line-through" : "text-ink";
  const docNo = (delivery: Delivery) =>
    delivery.invoiceNumber || t("inventoryPage.noDocNumber");
  const warehouses = (delivery: Delivery) => warehouseNamesOf(delivery).join(", ") || "—";

  // „Връзки": фактура → обобщените стокови; стокова → фактурата ѝ, „чака
  // фактура" или „приключена без фактура"
  const links = (delivery: Delivery) => {
    const noteState = noteInvoiceStateOf(delivery);
    if (noteState === "inInvoice")
      return (
        <span className="truncate rounded-md border border-info/30 bg-info-tint px-1.5 py-px text-xs leading-[18px] text-ink">
          ↑ {t("inventoryPage.linkInvoice", { id: delivery.invoiceId })}
        </span>
      );
    if (noteState === "closed")
      return (
        <span className="truncate text-xs text-muted" title={delivery.closedWithoutInvoiceReason ?? undefined}>
          {t("inventoryPage.linkClosedNoInvoice")}
        </span>
      );
    if (noteState === "waiting")
      return <span className="truncate text-xs text-warn-ink">{t("inventoryPage.linkWaitingInvoice")}</span>;
    const notes = delivery.linkedNoteIds ?? [];
    if (!notes.length) return <span className="text-faint">—</span>;
    return (
      <span
        className="flex min-w-0 items-center gap-1 overflow-hidden"
        title={notes.map((noteId) => t("inventoryPage.linkNote", { id: noteId })).join(", ")}
      >
        {notes.slice(0, MAX_LINKED_NOTES).map((noteId) => (
          <span
            key={noteId}
            className="shrink-0 rounded-md border border-info/30 bg-info-tint px-1.5 py-px text-xs leading-[18px] text-ink"
          >
            ↓ {t("inventoryPage.linkNote", { id: noteId })}
          </span>
        ))}
        {notes.length > MAX_LINKED_NOTES && (
          <span className="shrink-0 text-xs text-muted">+{notes.length - MAX_LINKED_NOTES}</span>
        )}
      </span>
    );
  };

  if (density === "cards") {
    return (
      <div onKeyDown={moveRowFocus}>
        {rows.map((delivery) => (
          <div
            key={delivery.id}
            {...openableRowProps(() => onOpen(delivery))}
            className="flex cursor-pointer flex-col gap-[5px] border-t border-shade-1 px-3.5 py-3 outline-none focus-visible:bg-fill"
          >
            <div className="flex items-center gap-2">
              <DeliveryTypeBadge documentType={delivery.documentType} />
              <span className={`flex-1 truncate font-semibold ${numberClass(delivery)}`}>
                {delivery.supplierName || "—"}
              </span>
              <DeliveryPaymentPill delivery={delivery} />
              <RowActionsMenu actions={actionsOf(delivery)} compact />
            </div>
            <div className="flex justify-between gap-2.5 text-[12.5px] text-body">
              <span>
                {docNo(delivery)} · {dayjs(delivery.date).format("DD.MM.YYYY")}
              </span>
              <span className={`font-sheet text-sm font-semibold ${numberClass(delivery)}`}>
                {formatMoney(deliveryGrossOf(delivery))}
              </span>
            </div>
            {delivery.note && (
              <div className="truncate text-[12.5px] text-muted" title={delivery.note}>
                {delivery.note}
              </div>
            )}
          </div>
        ))}
        {footer}
      </div>
    );
  }

  const grid = GRID_CLASS[density];
  const full = density === "full";
  const head = (
    label: string,
    options: { sortKey?: DeliverySortKey; align?: "right"; filters?: ReturnType<typeof sectionList> } = {},
  ) => <ReportHead label={label} sort={sort} onSort={onSort} {...options} />;

  return (
    <div className="overflow-x-auto">
      <div className={full ? "min-w-[1320px]" : "min-w-[800px]"} onKeyDown={moveRowFocus}>
        <div className={`${grid} ${REPORT_HEADER_ROW_CLASS}`}>
          {head("№")}
          {head(t("inventoryPage.colDate"), { sortKey: "date" })}
          {head(t("inventoryPage.colDocument"), {
            sortKey: "doc",
            filters: full
              ? sectionList(filters.types)
              : sectionList(filters.types, filters.warehouses, filters.users, filters.invoiceStates),
          })}
          {head(t("inventoryPage.colSupplier"), { sortKey: "supplier", filters: sectionList(filters.suppliers) })}
          {full && (
            <>
              {head(t("inventoryPage.colWarehouse"), { filters: sectionList(filters.warehouses) })}
              {head(t("inventoryPage.filterEnteredBy"), { filters: sectionList(filters.users) })}
              {head(t("inventoryPage.colLinks"), { filters: sectionList(filters.invoiceStates) })}
              {head(t("inventoryPage.fieldNote"))}
              {head(t("inventoryPage.colNet"), { align: "right" })}
            </>
          )}
          {head(t("inventoryPage.colGross"), { sortKey: "sum", align: "right", filters: sectionList(filters.gross) })}
          {head(t("inventoryPage.colPayment"), { filters: sectionList(filters.pays) })}
          <span />
        </div>
        {rows.map((delivery) => (
          <div
            key={delivery.id}
            {...openableRowProps(() => onOpen(delivery))}
            className={`${grid} ${REPORT_OPENABLE_ROW_CLASS}`}
          >
            <span className="font-sheet text-body">{delivery.id}</span>
            <span className="font-sheet text-ink-2">{dayjs(delivery.date).format("DD.MM.YYYY")}</span>
            <span className="flex min-w-0 items-center gap-2">
              <DeliveryTypeBadge documentType={delivery.documentType} />
              <span className={`shrink-0 font-medium ${numberClass(delivery)}`}>{docNo(delivery)}</span>
              {!full && (
                <span className="min-w-0 truncate text-muted" title={delivery.note ?? undefined}>
                  · {warehouses(delivery)} · {delivery.createdByName || "—"}
                  {delivery.note ? ` · ${delivery.note}` : ""}
                </span>
              )}
            </span>
            <span className="truncate text-ink">{delivery.supplierName || "—"}</span>
            {full && (
              <>
                <span className="truncate text-ink-2">{warehouses(delivery)}</span>
                <span className="truncate text-body">{delivery.createdByName || "—"}</span>
                <span className="flex min-w-0">{links(delivery)}</span>
                <span className="truncate text-body" title={delivery.note ?? undefined}>
                  {delivery.note || <span className="text-faint">—</span>}
                </span>
                <span className={`text-right font-sheet ${numberClass(delivery)}`}>
                  {formatMoney(deliveryNetOf(delivery))}
                </span>
              </>
            )}
            <span className={`text-right font-sheet font-semibold ${numberClass(delivery)}`}>
              {formatMoney(deliveryGrossOf(delivery))}
            </span>
            <span className="flex min-w-0">
              <DeliveryPaymentPill delivery={delivery} />
            </span>
            <RowActionsMenu actions={actionsOf(delivery)} onPrint={() => onPrint(delivery)} dense />
          </div>
        ))}
        {footer}
      </div>
    </div>
  );
};

export default DeliveriesReportTable;
